import type {
	SearchSelectionEvent,
	SelectAddressEvent
} from '~/composables/useNewSearchSuggest';

import { useTrackSearchPropertyEvents } from '~/composables/events/useTrackSearchPropertyEvents';
import { useSearchSuggestGeoOnlyConfig } from '~/composables/searchSuggest/useSearchSuggestGeoOnlyConfig';
import { buildShortsLocationPath } from '~/composables/useShortsFilters/buildShortsLocationPath';
import { stripShortsLocationPathFromRoute } from '~/composables/useShortsFilters/stripShortsLocationQuery';
import { useShortsFilters } from '~/composables/useShortsFilters/useShortsFilters';
import { MixpanelProps } from '~/core/service/mixpanel';

import { useShortsFiltersSearch } from '~/components/Filters/ShortsFiltersBar/composables/useShortsFiltersSearch';

const FILTER_TYPE_MAP = {
	macroregion: 'region',
	city: 'city',
	region: 'neighborhood'
} as const;

const NAME_KEY_MAP = {
	macroregion: MixpanelProps.REGION_NAME,
	city: MixpanelProps.CITY_NAME,
	region: MixpanelProps.NEIGHBORHOODS_NAME
} as const;

function buildPendingTrackingPayload(event: SelectAddressEvent) {
	const { searchType, title, textSearched } = event;

	const filterApplied =
		searchType && searchType in FILTER_TYPE_MAP
			? FILTER_TYPE_MAP[searchType as keyof typeof FILTER_TYPE_MAP]
			: 'neighborhood';

	const nameEntry =
		searchType && searchType in NAME_KEY_MAP
			? { [NAME_KEY_MAP[searchType as keyof typeof NAME_KEY_MAP]]: title || '' }
			: {};

	return {
		filterApplied,
		nameEntry,
		textSearched: textSearched ?? ''
	};
}

/**
 * Wires search suggest to the fullscreen video page (`/shorts/[id]`).
 *
 * - User picks city/region/etc. → refs come from the same place as the home bar
 *   (`useShortsFilters` + `useShortsFiltersSearch` bus, like `ShortsFiltersBar`).
 * - Shorts listing lives at `/shorts`; on the reel you are on another route, so after a pick
 *   we `navigateTo('/shorts', { query })` to show the filtered grid.
 * - On mobile the dialog has no visible button (header opens it); `isSearchDialogOpen` and the
 *   template ref exist to focus the input when it opens.
 *
 * This composable's bus listener and the one inside `useShortsFiltersSearch` are independent
 * (two `.on` on the same bus); each registers its own `unsubscribe` in `onUnmounted`.
 * Tracking for `filter_applied` is deferred to `Page.vue` via sessionStorage so the
 * `total_results` reflects the real count after the fetch on `/shorts` completes.
 */
export function useShortsReelLocationSearch() {
	/** Avoid `navigateTo` if `nextTick` runs after unmount (ex.: fast navigation outside the reel). */
	let isReelLocationSearchActive = true;

	const isSearchDialogOpen = ref(false);

	const shortsMobileSearchDialogRef = useTemplateRef<{
		focusInput: () => void;
	}>('shortsMobileSearchDialog');

	const stopWatchDialog = watch(isSearchDialogOpen, async open => {
		if (!open || !import.meta.client) return;
		await nextTick();
		shortsMobileSearchDialogRef.value?.focusInput();
	});

	const shortsSearchGeoOnly = useSearchSuggestGeoOnlyConfig();

	const {
		cities,
		regions,
		macroregions,
		states,
		propertyTypes,
		selectionText,
		currentFiltersObject,
		locationLabel,
		clearLocation
	} = useShortsFilters();
	const { trackEmptyStateFilter, trackFiltersAppliedResumed } =
		useTrackSearchPropertyEvents();

	function handleEmptyResultOnFilter(searchTerm: string) {
		trackEmptyStateFilter({
			[MixpanelProps.TEXT_SEARCH]: searchTerm
		});
	}

	// Tracking is deferred to `/shorts` (see docblock).
	const { shortsSearchSuggestBus, handleSearchTermUpdate } =
		useShortsFiltersSearch(cities, regions, macroregions, states, {
			skipTracking: true
		});

	function navigateToShortsHomeWithLocationPath(): void {
		const { path, query } = buildShortsLocationPath({
			macroregions: macroregions.value,
			cities: cities.value,
			regions: regions.value,
			states: states.value,
			propertyTypes: propertyTypes.value
		});
		void navigateTo({ path, query }, { replace: true });
	}

	const stopShortsSearchNavigateHome = onClientEventBus(
		shortsSearchSuggestBus,
		(event: SearchSelectionEvent) => {
			if (event.type === 'address' || event.type === 'place') {
				const { filterApplied, nameEntry, textSearched } =
					buildPendingTrackingPayload(event.payload);
				trackFiltersAppliedResumed({
					[MixpanelProps.FILTERS_APPLIED]: filterApplied,
					[MixpanelProps.CURRENT_PAGE]: 'Shorts Feed',
					[MixpanelProps.FILTERED_BY_OPEN_FILTERS]: false,
					[MixpanelProps.FILTERED_BY_SEARCH_BAR]: true,
					[MixpanelProps.FILTERED_BY_QUICK_FILTER]: false,
					[MixpanelProps.FILTERED_BY_MORE_FILTERS]: false,
					[MixpanelProps.TEXT_SEARCHED]: textSearched,
					...nameEntry
				});
				nextTick(() => {
					if (!isReelLocationSearchActive) return;
					navigateToShortsHomeWithLocationPath();
				});
			}
		}
	);

	onUnmounted(() => {
		isReelLocationSearchActive = false;
		stopWatchDialog();
		stopShortsSearchNavigateHome();
	});

	function handleShortsSearchClearLocation(): void {
		clearLocation();
		stripShortsLocationPathFromRoute();
	}

	return {
		isSearchDialogOpen,
		shortsSearchGeoOnly,
		selectionText,
		currentFiltersObject,
		cities,
		regions,
		macroregions,
		states,
		locationLabel,
		shortsSearchSuggestBus,
		handleSearchTermUpdate,
		handleShortsSearchClearLocation,
		handleEmptyResultOnFilter
	};
}
